/**
 * Auth service — wraps Firebase Auth operations with friendly error messages.
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  type User,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { DEFAULT_USER_PERMISSIONS } from '@/types';

// ─── Error message mapping ────────────────────────────────────────────────────

function parseAuthError(code: string): string {
  const messages: Record<string, string> = {
    'auth/email-already-in-use':    'An account with this email already exists.',
    'auth/invalid-email':           'Please enter a valid email address.',
    'auth/weak-password':           'Password must be at least 6 characters.',
    'auth/user-not-found':          'No account found with this email address.',
    'auth/wrong-password':          'Incorrect password. Please try again.',
    'auth/invalid-credential':      'Incorrect email or password. Please try again.',
    'auth/too-many-requests':       'Too many failed attempts. Please try again later.',
    'auth/user-disabled':           'This account has been disabled.',
    'auth/network-request-failed':  'Network error. Please check your connection.',
    'auth/popup-closed-by-user':    'Sign-in was cancelled.',
  };
  return messages[code] ?? 'An unexpected error occurred. Please try again.';
}

function handleAuthError(err: unknown): never {
  const code = (err as { code?: string }).code ?? '';
  throw new Error(parseAuthError(code));
}

// ─── Register ─────────────────────────────────────────────────────────────────

export interface RegisterInput {
  displayName: string;
  email:       string;
  password:    string;
}

/**
 * Registers a new user with Firebase Auth and creates their Firestore user doc.
 * The Cloud Function `onUserCreate` also fires and sets status to 'pending' —
 * this write here is a fallback in case of Function delay.
 */
export async function registerUser(input: RegisterInput): Promise<User> {
  try {
    const { user } = await createUserWithEmailAndPassword(
      auth,
      input.email,
      input.password
    );

    // Update display name in Firebase Auth
    await updateProfile(user, { displayName: input.displayName });

    // Create Firestore user doc (Cloud Function will also do this, but we
    // write it here immediately so the UI doesn't wait for the Function)
    await setDoc(doc(db, 'users', user.uid), {
      uid:           user.uid,
      email:         input.email,
      displayName:   input.displayName,
      photoURL:      null,
      status:        'pending',
      role:          'viewer',
      permissions:   DEFAULT_USER_PERMISSIONS,
      createdAt:     serverTimestamp(),
      approvedAt:    null,
      approvedBy:    null,
      lastLoginAt:   serverTimestamp(),
      deactivatedAt: null,
      deactivatedBy: null,
    });

    return user;
  } catch (err) {
    handleAuthError(err);
  }
}

// ─── Login ────────────────────────────────────────────────────────────────────

export interface LoginInput {
  email:    string;
  password: string;
}

export async function loginUser(input: LoginInput): Promise<User> {
  try {
    const { user } = await signInWithEmailAndPassword(
      auth,
      input.email,
      input.password
    );
    return user;
  } catch (err) {
    handleAuthError(err);
  }
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export async function logoutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (err) {
    handleAuthError(err);
  }
}

// ─── Password reset ───────────────────────────────────────────────────────────

export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (err) {
    handleAuthError(err);
  }
}
